<script setup lang="ts">
interface Props {
  fallbackRoute?: string;
  /**
   * When true, navigate using router.replace instead of router.push for the
   * fallback route. Used on forward-only pages (change-maker, premium store)
   * so the page they're leaving is dropped from the back stack and the
   * browser back button can never rewind into it.
   */
  replace?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fallbackRoute: "",
  replace: false,
});

const uiStore = useUiStore();
const router = useRouter();

const hasLastRoute = computed(() => {
  return uiStore.lastRoute && uiStore.lastRoute !== "/";
});

const showButton = computed(() => {
  return hasLastRoute.value || !!props.fallbackRoute;
});

// An explicit fallbackRoute is the canonical parent for this page and
// always wins. Without that, pages like /orders/upcoming would sometimes
// router.back() into /premium/schedule/change-maker — which is a one-way
// forward action and should not be a Back destination. Pages that genuinely
// live below a free navigation graph (e.g. /search/[slug]) leave
// fallbackRoute empty and fall through to the browser history.
//
// uiStore.lastRoute is a last resort because it's overwritten on every
// navigation — pushing it would walk forward-then-back-then-forward.
function goBack() {
  if (props.fallbackRoute) {
    if (props.replace) {
      router.replace(props.fallbackRoute);
    } else {
      router.push(props.fallbackRoute);
    }
    return;
  }
  if (import.meta.client && window.history.length > 1) {
    router.back();
    return;
  }
  if (hasLastRoute.value) {
    router.push(uiStore.lastRoute);
  }
}
</script>

<template>
  <a
    v-if="showButton"
    class="back-btn"
    @click.prevent="goBack"
  >
    <fa
      class="small-icon"
      :icon="['fal', 'arrow-left']"
    />
    Back
  </a>
</template>

<style lang="scss">
// Intentionally NOT scoped: the element+class selector already guarantees
// uniqueness, and keeping this global makes the override bullet-proof against
// the global `a, .link { color: inherit }` rule in main.scss.
// Style-guide: link uses foreground ink, primary on hover.
a.back-btn,
.back-btn {
  cursor: pointer;
  background: none;
  border: none;
  display: inline-flex;
  align-items: center;
  margin: 0 0 1.5rem;
  color: var(--color-grey-1) !important;
  font-weight: var(--weight-semibold);
  transition: color var(--dur-fast) var(--ease-out);

  svg,
  .small-icon {
    margin-right: 0.5rem;
    color: var(--color-grey-1) !important;
    transition: color var(--dur-fast) var(--ease-out);
  }

  &:hover,
  &:focus {
    text-decoration: underline;
    color: var(--color-pink-1) !important;
    svg,
    .small-icon {
      color: var(--color-pink-1) !important;
    }
  }
}
</style>
